import { QueryClient } from '@tanstack/react-query'
import { createRateLimiter, RateLimiter } from 'src/queries/pusherRateLimiter'
import { statisticsQueryKeys } from 'src/queries/queries.constants'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'

/**
 * Credit-card statistics Pusher sync. A CREDIT_CARD event invalidates the five
 * aggregate stat keys the card mutations move. Routed through a shared,
 * per-session rate limiter so a burst (a bulk card closure fires one event per
 * card) can't refetch /statistics unbounded and 429 the shared vault — the
 * ceiling AGENTS.md "Pusher → React Query sync" guards. Over budget degrades to
 * `refetchType: 'none'` (mark stale, reconcile on next focus/mount), never a
 * refetch-now storm.
 *
 * Unlike the entity families (virtualCards), this is invalidation-only: there
 * is no per-id detail fetch to await, so there is no upstream 429 to observe
 * and no circuit breaker — the limiter alone bounds request volume.
 */

const CREDIT_CARD_PUSHER_STATISTICS_KEYS = [
  StatisticsKeys.CreditCardsDelegateCount,
  StatisticsKeys.CreditCardsActiveSourceCount,
  StatisticsKeys.CreditCardsSourceCount,
  StatisticsKeys.CreditCardsDelegateUserMeCount,
  StatisticsKeys.CreditCardsActiveDelegateCount,
]

// One budget per session for the whole credit-card statistics family.
const creditCardStatisticsLimiter = createRateLimiter()

/**
 * Build the CREDIT_CARD statistics refresh handler. Call the returned function
 * once per CREDIT_CARD Pusher event: within budget it invalidates the five stat
 * keys (default `active` refetch — only mounted consumers refetch); over budget
 * it marks them stale without refetching now.
 */
export const createCreditCardStatisticsPusherSync = ({
  queryClient,
  limiter = creditCardStatisticsLimiter,
}: {
  queryClient: QueryClient
  limiter?: RateLimiter
}): (() => void) => {
  return () => {
    const refetchType = limiter.tryAcquire() ? 'active' : 'none'
    CREDIT_CARD_PUSHER_STATISTICS_KEYS.forEach((statisticKey) => {
      // Reactive burst path: coalesce onto the in-flight /statistics fetch
      // instead of aborting + relaunching one per event.
      queryClient.invalidateQueries(
        {
          queryKey: statisticsQueryKeys.list([statisticKey]),
          refetchType,
        },
        { cancelRefetch: false },
      )
    })
  }
}
